if (!customElements.get('bemalt-pdp-v2-actions')) {
  customElements.define('bemalt-pdp-v2-actions', class BemaltPdpV2Actions extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.purchase = this.querySelector('[data-bemalt-v2-purchase]');
      this.quote = this.querySelector('[data-bemalt-v2-quote]');
      this.quoteAction = this.querySelector('[data-bemalt-v2-quote-action]');
      this.deliveryQuoteAction = this.productInfo?.querySelector('[data-bemalt-national-shipping-quote]');
      this.quoteSupport = this.querySelector('[data-bemalt-v2-quote-support]');
      this.notReady = this.querySelector('[data-bemalt-v2-not-ready]');
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onPickerChange = this.onPickerChange.bind(this);
      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.onQuantityUpdate = this.onQuantityUpdate.bind(this);
      this.selectionGeneration = 0;
      this.activeSelection = null;
      this.deliveryOptions = [];
      this.deliveryVariant = null;
      this.deliveryQuantity = null;
      this.overrideConfig = this.forcedQuoteOverrideConfig();
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      this.unsubscribeQuantityUpdate = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityUpdate);
      // Capture runs before VariantSelects publishes to product-info, so the request
      // created by Dawn is associated with this exact interactive selection.
      this.productInfo?.addEventListener('change', this.onPickerChange, true);
      this.productInfo?.addEventListener('input', this.onQuantityChange);
      this.productInfo?.addEventListener('change', this.onQuantityChange);
      this.installProductInfoResponseGuard();
      this.protectPickupAvailability();
      this.deliveryOptions = this.selectedDeliveryOptions();
      this.deliveryQuantity = this.selectedDeliveryQuantity();
      this.render(this.selectedVariant());
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribeQuantityUpdate?.();
      this.productInfo?.removeEventListener('change', this.onPickerChange, true);
      this.productInfo?.removeEventListener('input', this.onQuantityChange);
      this.productInfo?.removeEventListener('change', this.onQuantityChange);
      this.removeProductInfoResponseGuard();
    }

    selectedVariant() {
      try { return JSON.parse(this.querySelector('[data-bemalt-v2-selected-variant]')?.textContent); } catch { return null; }
    }

    /**
     * TEMPORARY FORCED-QUOTE OVERRIDE
     *
     * PDP V2 resolves a selection's effective commercial mode before Dawn
     * handles it. `Contenido` = `200 L` temporarily becomes quote regardless
     * of the immutable product base mode. Remove this integration when
     * sales_mode is migrated to ProductVariant metafields.
     */
    forcedQuoteOverrideConfig() {
      try {
        return JSON.parse(this.productInfo?.querySelector('[data-bemalt-v2-forced-quote-config]')?.textContent) || {};
      } catch {
        return {};
      }
    }

    effectiveSalesMode(selectedOptions = null) {
      if (!selectedOptions && this.activeSelection) return this.activeSelection.effectiveSalesMode;
      const resolver = window.BemaltForcedQuoteOverride?.getEffectiveSalesMode;
      // TEMPORARY FORCED-QUOTE OVERRIDE: fail closed if the temporary
      // resolver/config is unavailable; never expose a forced presentation for purchase.
      if (typeof resolver !== 'function' || !this.overrideConfig?.optionName || !Array.isArray(this.overrideConfig.forcedQuoteValues)) return 'quote';
      const currentOptions = selectedOptions || this.selectedOptions(this.activeVariantPicker());
      if (!selectedOptions && currentOptions.length === 0) {
        return this.dataset.effectiveSalesMode || 'quote';
      }
      return resolver({
        baseSalesMode: this.dataset.baseSalesMode,
        selectedOptions: currentOptions,
        overrideConfig: this.overrideConfig,
      });
    }

    onVariantChange({ data: { sectionId, variant } }) {
      if (sectionId !== this.productInfo?.dataset.section) return;
      if (this.activeSelection && !this.optionsMatch(variant, this.activeSelection.options)) return;
      if (variant) this.productInfo?.setQuantityBoundries?.();
      this.render(variant);
    }

    onPickerChange(event) {
      if (!(event.target instanceof Element)) return;

      if (event.target.matches('input.product-variant-id') && !event.target.value && this.activeSelection) {
        const picker = this.activeVariantPicker();
        const selectedOptions = picker && this.selectedOptions(picker);
        if (selectedOptions?.length === this.activeSelection.options.length && selectedOptions.every((option, index) => option.value === this.activeSelection.options[index]?.value)) {
          this.renderNeutralState();
        }
        return;
      }

      const picker = event.target.closest('variant-selects');
      if (!picker || !this.productInfo?.contains(picker) || picker.hidden || picker.style.display === 'none') return;

      this.activeSelection = {
        generation: ++this.selectionGeneration,
        options: this.selectedOptions(picker),
      };
      this.deliveryOptions = this.selectedDeliveryOptions();
      this.deliveryVariant = null;
      this.deliveryQuantity = this.selectedDeliveryQuantity();
      this.activeSelection.effectiveSalesMode = this.effectiveSalesMode(this.activeSelection.options);
      this.renderPendingState();
    }

    installProductInfoResponseGuard() {
      if (!this.productInfo || this.productInfo.bemaltV2RenderProductInfoGuard) return;

      const dawnRenderProductInfo = this.productInfo.renderProductInfo?.bind(this.productInfo);
      if (!dawnRenderProductInfo) return;

      const guardedRenderProductInfo = (request) => {
        const generation = this.activeSelection?.generation;
        const callback = request.callback;

        return dawnRenderProductInfo({
          ...request,
          callback: (html) => {
            // Dawn mutates picker, pickup, URL, variant ID and price inside this
            // callback. Reject stale responses before any of those mutations run.
            if (generation && generation !== this.activeSelection?.generation) return;
            callback(html);
          },
        });
      };

      this.productInfo.bemaltV2RenderProductInfoGuard = {
        dawnRenderProductInfo,
        guardedRenderProductInfo,
      };
      this.productInfo.renderProductInfo = guardedRenderProductInfo;
    }

    removeProductInfoResponseGuard() {
      const guard = this.productInfo?.bemaltV2RenderProductInfoGuard;
      if (!guard || this.productInfo.renderProductInfo !== guard.guardedRenderProductInfo) return;
      this.productInfo.renderProductInfo = guard.dawnRenderProductInfo;
      delete this.productInfo.bemaltV2RenderProductInfoGuard;
    }

    selectedOptions(picker) {
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('select')).map((select) => ({
        name: select.selectedOptions[0]?.dataset.optionName || select.dataset.optionName || '',
        value: select.value,
      }));
    }

    optionsMatch(variant, selectedOptions) {
      return Boolean(
        variant
        && variant.options?.length === selectedOptions.length
        && variant.options.every((value, index) => value === selectedOptions[index]?.value)
      );
    }

    onQuantityChange(event) {
      if (!event.target.matches('.quantity__input')) return;
      if (this.currentVariant) this.updateQuotePayload(this.currentVariant);
      this.deliveryQuantity = this.selectedDeliveryQuantity();
      this.updateDeliveryQuotePayload(this.deliveryVariant);
    }

    onQuantityUpdate() {
      if (this.currentVariant) this.updateQuotePayload(this.currentVariant);
      queueMicrotask(() => {
        if (!this.variantMatchesDeliveryOptions(this.currentVariant) || !this.pickerMatchesDeliveryOptions()) return;
        this.deliveryQuantity = this.selectedDeliveryQuantity();
        this.updateDeliveryQuotePayload(this.deliveryVariant);
      });
    }

    isPickupEligible(variant) {
      return Boolean(variant && variant.available && this.effectiveSalesMode() !== 'quote');
    }

    protectPickupAvailability() {
      customElements.whenDefined('pickup-availability').then(() => {
        const pickup = this.productInfo?.querySelector('[data-bemalt-v2-pickup]');
        if (!pickup || pickup.dataset.bemaltV2PickupProtected) return;

        const dawnRenderPreview = pickup.renderPreview?.bind(pickup);
        const dawnRenderError = pickup.renderError?.bind(pickup);
        if (!dawnRenderPreview || !dawnRenderError) return;

        pickup.dataset.bemaltV2PickupProtected = 'true';
        /**
         * TEMPORARY FORCED-QUOTE OVERRIDE
         *
         * `Contenido` = `200 L` SSR starts as quote without `available`, so
         * Dawn does not initialize pickup's error state. Supply the same local
         * state before a later 200 L → 19 L fetch failure, without editing
         * pickup-availability.js. Remove this bridge when sales_mode is
         * migrated to ProductVariant metafields.
         */
        if (!pickup.errorHtml) {
          const errorTemplate = pickup.querySelector('template');
          pickup.errorHtml = errorTemplate?.content.firstElementChild?.cloneNode(true);
        }
        if (!Object.prototype.hasOwnProperty.call(pickup, 'onClickRefreshList')) {
          pickup.onClickRefreshList = pickup.constructor.prototype.onClickRefreshList?.bind(pickup);
        }
        pickup.bemaltV2PickupRequestVersion = 0;
        pickup.bemaltV2InitialRequestPending = pickup.hasAttribute('available');
        pickup.bemaltV2InitialRequestVariantId = pickup.dataset.variantId || '';
        pickup.dataset.bemaltV2PickupVariantId = String(this.currentVariant?.id || pickup.dataset.variantId || '');

        const clearPickup = () => {
          pickup.removeAttribute('available');
          pickup.innerHTML = '';
          const drawer = document.querySelector('pickup-availability-drawer');
          drawer?.hide?.();
          drawer?.remove();
        };

        const isCurrentPickupRequest = (variantId, requestVersion) => (
          pickup.isConnected
          && requestVersion === pickup.bemaltV2PickupRequestVersion
          && this.isPickupEligible(this.currentVariant)
          && String(this.currentVariant?.id) === String(variantId)
          && pickup.dataset.bemaltV2PickupVariantId === String(variantId)
        );

        pickup.renderPreview = (sectionInnerHTML) => {
          if (!pickup.bemaltV2InitialRequestPending) return;

          pickup.bemaltV2InitialRequestPending = false;
          if (isCurrentPickupRequest(pickup.bemaltV2InitialRequestVariantId, pickup.bemaltV2PickupRequestVersion)) {
            dawnRenderPreview(sectionInnerHTML);
          }
        };

        pickup.renderError = () => {
          if (!pickup.bemaltV2InitialRequestPending) return;

          pickup.bemaltV2InitialRequestPending = false;
          if (isCurrentPickupRequest(pickup.bemaltV2InitialRequestVariantId, pickup.bemaltV2PickupRequestVersion)) {
            dawnRenderError();
          }
        };

        pickup.fetchAvailability = (variantId) => {
          if (!variantId) return;

          const requestVersion = ++pickup.bemaltV2PickupRequestVersion;
          let rootUrl = pickup.dataset.rootUrl;
          if (!rootUrl.endsWith('/')) rootUrl += '/';
          const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

          fetch(variantSectionUrl)
            .then((response) => response.text())
            .then((text) => {
              const sectionInnerHTML = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector('.shopify-section');
              if (!isCurrentPickupRequest(variantId, requestVersion)) return;
              dawnRenderPreview(sectionInnerHTML);
            })
            .catch(() => {
              if (isCurrentPickupRequest(variantId, requestVersion)) dawnRenderError();
            });
        };

        pickup.update = (variant) => {
          if (!this.isPickupEligible(variant)) {
            ++pickup.bemaltV2PickupRequestVersion;
            pickup.dataset.bemaltV2PickupVariantId = '';
            clearPickup();
            return;
          }

          pickup.dataset.bemaltV2PickupVariantId = String(variant.id);
          pickup.dataset.variantId = variant.id;
          pickup.fetchAvailability(variant.id);
        };
      });
    }

    render(variant) {
      if (!variant) {
        this.renderNeutralState();
        return;
      }

      this.currentVariant = variant;
      if (this.variantMatchesDeliveryOptions(variant)) this.deliveryQuantity = this.selectedDeliveryQuantity();
      this.updateDeliveryQuotePayload(variant);
      const effectiveSalesMode = this.effectiveSalesMode();
      const purchasable = effectiveSalesMode !== 'quote' && Boolean(variant.available);
      const availability = this.productInfo?.querySelector('[data-bemalt-v2-availability]');
      const quantityContainer = this.productInfo?.querySelector('[data-bemalt-v2-quantity]');
      const state = variant.available ? 'available' : 'quote';
      const message = variant.available ? 'Disponible' : 'Consulte vía cotización';
      if (availability) { availability.dataset.state = state; availability.textContent = message; }
      if (quantityContainer) quantityContainer.hidden = false;
      this.purchase.hidden = !purchasable;
      const showQuote = effectiveSalesMode === 'quote' || !variant.available || effectiveSalesMode === 'hybrid';
      this.quote.hidden = !showQuote;
      this.notReady.hidden = true;
      if (!showQuote) return;
      const consultation = effectiveSalesMode !== 'quote' && !variant.available;
      this.quoteAction.textContent = consultation ? 'Consultar disponibilidad' : 'Solicitar cotización';
      this.quoteAction.classList.toggle('button--primary', !purchasable);
      this.quoteAction.classList.toggle('button--secondary', purchasable);
      this.quoteSupport.textContent = consultation ? 'Podemos revisar disponibilidad y plazo de entrega para esta opción.' : this.dataset.quoteSupportText || 'Podemos ayudarte a revisar disponibilidad y condiciones comerciales.';
      this.updateQuotePayload(variant, consultation);
    }

    invalidateQuotePayload() {
      this.quoteAction.removeAttribute('href');
      this.quoteAction.removeAttribute('target');
      this.quoteAction.removeAttribute('rel');
      this.quoteAction.setAttribute('aria-disabled', 'true');
    }

    renderPendingState() {
      this.currentVariant = null;
      this.invalidateQuotePayload();
      this.updateDeliveryQuotePayload();
      this.neutralizeVariantIdentity();
      this.neutralizePickupAvailability();

      const availability = this.productInfo?.querySelector('[data-bemalt-v2-availability]');
      if (availability) {
        availability.dataset.state = 'pending';
        availability.textContent = 'Actualizando selección…';
      }

      const price = this.productInfo?.querySelector('[id^="price-"]');
      if (price) price.innerHTML = '<p class="bemalt-pdp-v2__price-unavailable">Actualizando selección…</p>';

      const quantityContainer = this.productInfo?.querySelector('[data-bemalt-v2-quantity]');
      if (quantityContainer) quantityContainer.hidden = true;
      this.purchase.hidden = true;
      this.quote.hidden = false;
      this.quoteAction.textContent = this.effectiveSalesMode() === 'quote' ? 'Solicitar cotización' : 'Consultar disponibilidad';
      this.quoteAction.classList.add('button--primary');
      this.quoteAction.classList.remove('button--secondary');
      this.quoteSupport.textContent = 'Actualizando las opciones seleccionadas…';
      this.notReady.hidden = true;
    }

    neutralizeVariantIdentity() {
      if (!this.productInfo) return;

      this.productInfo.querySelectorAll('input.product-variant-id, input[name="id"]').forEach((input) => {
        input.value = '';
      });

      const productUrl = this.productInfo.dataset.url;
      if (!productUrl) return;
      this.productInfo.querySelector('share-button')?.updateUrl(`${window.shopUrl}${productUrl}`);
      if (this.productInfo.dataset.updateUrl !== 'false') {
        window.history.replaceState({}, '', productUrl);
      }
    }

    neutralizePickupAvailability() {
      const pickup = this.productInfo?.querySelector('[data-bemalt-v2-pickup]');
      if (!pickup) return;

      // Invalidate both protected requests and any initial preview still in flight.
      pickup.bemaltV2InitialRequestPending = false;
      if (typeof pickup.bemaltV2PickupRequestVersion === 'number') {
        pickup.bemaltV2PickupRequestVersion += 1;
      }
      pickup.dataset.bemaltV2PickupVariantId = '';

      if (typeof pickup.update === 'function') {
        pickup.update(null);
        return;
      }

      pickup.removeAttribute('available');
      pickup.innerHTML = '';
      const drawer = document.querySelector('pickup-availability-drawer');
      drawer?.hide?.();
      drawer?.remove();
    }

    renderNeutralState() {
      this.currentVariant = null;
      this.updateDeliveryQuotePayload();
      this.neutralizePickupAvailability();

      const availability = this.productInfo?.querySelector('[data-bemalt-v2-availability]');
      if (availability) {
        availability.dataset.state = 'unavailable';
        availability.textContent = 'Selecciona una combinación válida.';
      }

      const price = this.productInfo?.querySelector('[id^="price-"]');
      if (price) price.innerHTML = '<p class="bemalt-pdp-v2__price-unavailable">Selecciona una combinación válida.</p>';

      const quantityContainer = this.productInfo?.querySelector('[data-bemalt-v2-quantity]');
      if (quantityContainer) quantityContainer.hidden = true;
      this.purchase.hidden = true;
      this.quote.hidden = false;
      const consultation = this.effectiveSalesMode() !== 'quote';
      this.quoteAction.textContent = consultation ? 'Consultar disponibilidad' : 'Solicitar cotización';
      this.quoteAction.classList.add('button--primary');
      this.quoteAction.classList.remove('button--secondary');
      this.quoteSupport.textContent = 'Podemos revisar disponibilidad, una alternativa equivalente y orientación comercial para esta combinación.';
      this.notReady.hidden = false;
      this.notReady.textContent = 'Selecciona una combinación válida.';
      this.invalidateQuotePayload();
      this.updateQuotePayload(null, consultation, true);
    }

    activeVariantPicker() {
      const pickers = Array.from(this.productInfo?.querySelectorAll('variant-selects') || []);
      return pickers.find((picker) => picker.isConnected && !picker.hidden && picker.style.display !== 'none') || null;
    }

    selectedOptionLines(picker) {
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('select')).map((select) => {
        const label = Array.from(this.productInfo.querySelectorAll('label')).find(({ htmlFor }) => htmlFor === select.id)
          ?.textContent?.trim();
        return `${label || select.dataset.optionName || 'Opción'}: ${select.value}`;
      });
    }

    selectedDeliveryOptions() {
      const picker = this.activeVariantPicker();
      if (!picker) return [];
      return Array.from(picker.querySelectorAll('select')).map((select) => {
        const label = Array.from(this.productInfo.querySelectorAll('label')).find(({ htmlFor }) => htmlFor === select.id)
          ?.textContent?.trim();
        return { label: label || select.dataset.optionName || 'Opción', value: select.value };
      });
    }

    selectedDeliveryQuantity() {
      const input = this.productInfo?.querySelector('.quantity__input');
      if (!input || !input.validity.valid) return null;
      return input.value;
    }

    variantMatchesDeliveryOptions(variant) {
      if (!variant) return false;
      if (this.deliveryOptions.length === 0) {
        return variant.options?.length === 0 || (variant.options?.length === 1 && variant.options[0] === 'Default Title');
      }
      return variant.options?.length === this.deliveryOptions.length
        && variant.options.every((value, index) => value === this.deliveryOptions[index]?.value);
    }

    pickerMatchesDeliveryOptions() {
      const pickerOptions = this.selectedDeliveryOptions();
      return pickerOptions.length === this.deliveryOptions.length
        && pickerOptions.every(({ value }, index) => value === this.deliveryOptions[index]?.value);
    }

    invalidateDeliveryQuotePayload() {
      if (!this.deliveryQuoteAction) return;
      this.deliveryQuoteAction.removeAttribute('href');
      this.deliveryQuoteAction.removeAttribute('target');
      this.deliveryQuoteAction.removeAttribute('rel');
      this.deliveryQuoteAction.setAttribute('aria-disabled', 'true');
    }

    updateDeliveryQuotePayload(variant = this.currentVariant) {
      if (!this.deliveryQuoteAction) return;
      if (variant && !this.variantMatchesDeliveryOptions(variant)) return;
      if (variant) this.deliveryVariant = variant;
      else this.deliveryVariant = null;

      const href = window.BemaltNationalShipping?.buildQuoteHref({
        whatsappNumber: this.deliveryQuoteAction.dataset.whatsappNumber,
        productTitle: this.deliveryQuoteAction.dataset.productTitle,
        productVendor: this.deliveryQuoteAction.dataset.productVendor,
        options: this.deliveryOptions,
        variantId: this.deliveryVariant?.id,
        quantity: this.deliveryQuantity,
        productUrl: new URL(this.productInfo?.dataset.url || location.pathname, location.origin).href,
      });
      if (!href) { this.invalidateDeliveryQuotePayload(); return; }

      this.deliveryQuoteAction.href = href;
      this.deliveryQuoteAction.target = '_blank';
      this.deliveryQuoteAction.rel = 'noopener noreferrer';
      this.deliveryQuoteAction.removeAttribute('aria-disabled');
    }

    updateQuotePayload(variant, consultation = this.effectiveSalesMode() !== 'quote' && !variant?.available, nullVariant = false) {
      if (this.quote.hidden) return;
      const picker = this.activeVariantPicker();
      if (!picker) return;
      const options = this.selectedOptionLines(picker);
      const quantity = this.productInfo?.querySelector('.quantity__input')?.value || '1';
      const number = (this.dataset.whatsappNumber || '').replace(/\D/g, '');
      if (!/^\d{8,15}$/.test(number)) { this.invalidateQuotePayload(); return; }
      const request = nullVariant
        ? this.effectiveSalesMode() === 'quote'
          ? 'solicitar una cotización, una alternativa equivalente y orientación comercial'
          : 'consultar la disponibilidad, una alternativa equivalente y orientación comercial'
        : consultation ? 'consultar la disponibilidad' : 'solicitar una cotización';
      const productUrl = nullVariant
        ? new URL(this.productInfo?.dataset.url || location.pathname, location.origin).href
        : location.href;
      const messageLines = [
        `Hola, me interesa ${request} del siguiente producto:`,
        '',
        `Producto: ${this.dataset.productTitle || ''}`,
      ];
      if (this.dataset.productVendor) messageLines.push(`Marca: ${this.dataset.productVendor}`);
      messageLines.push(...options, `Cantidad: ${quantity}`, '', productUrl);
      const messageBody = messageLines.join('\n');
      this.quoteAction.href = `https://wa.me/${number}?text=${encodeURIComponent(messageBody)}`;
      this.quoteAction.target = '_blank'; this.quoteAction.rel = 'noopener noreferrer'; this.quoteAction.removeAttribute('aria-disabled');
    }
  });
}
